import { QueryKeys } from "@/constants/query-keys";
import { useGetDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement";
import { useGetDietaryRequirementOptions } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement-options";
import { useUpdateDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-update-dietary-requirement";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { IonContent, IonPage } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";
import { SelectablePill } from "../../components/selectable-pill";

const titleCase = (s: string) =>
  s.replace(/[-_]/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

const OnboardingDietaryPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { dietaryRequirement } = useGetDietaryRequirement();
  const { dietaryOptions } = useGetDietaryRequirementOptions();
  const queryClient = useQueryClient();
  const { updateDietaryRequirementAsync, isUpdating } = useUpdateDietaryRequirement();
  const { showError } = useToast();
  const hydratedRef = useRef(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (hydratedRef.current) return;
    if (dietaryRequirement?.dietaryOptions && dietaryRequirement.dietaryOptions.length > 0) {
      setSelected(new Set(dietaryRequirement.dietaryOptions));
      hydratedRef.current = true;
    }
  }, [dietaryRequirement?.dietaryOptions]);

  const toggle = (option: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isUpdating) return;
    try {
      await Promise.all([
        preloadNextRoute(),
        updateDietaryRequirementAsync({
          dietaryOptions: Array.from(selected),
          milkPreference: dietaryRequirement?.milkPreference ?? null,
        }),
      ]);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.DIETARY_REQUIREMENT] });
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/dietary.svg"
          title="What's your dietary preference?"
          subtitle="Select the option(s) that best suit you"
        >
          <div className="flex w-full flex-col items-center gap-[18px]">
            {dietaryOptions?.map((option) => (
              <SelectablePill
                key={option}
                label={titleCase(option)}
                variant={selected.has(option) ? "selected" : "default"}
                onClick={() => toggle(option)}
                disabled={isUpdating}
              />
            ))}
          </div>
          <OnboardingNextFab onClick={handleSubmit} disabled={isUpdating} />
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingDietaryPage;
